import { useCallback } from 'react';

/**
 * useDragDropReorder Hook
 * Generic hook for drag-and-drop reordering of arrays
 *
 * Works with @hello-pangea/dnd library (React 18+ compatible fork of react-beautiful-dnd)
 * Provides a reusable handler for reordering any array-based state
 *
 * @param {Function} setState - State setter function from useState
 * @param {Function} onReorder - Optional callback after reorder completes
 * @returns {Object} Drag-drop handlers
 */
const useDragDropReorder = (setState, onReorder) => {
  /**
   * Handle drag end event
   * Reorders array items based on drag-drop result
   *
   * @param {Object} result - Drag result from @hello-pangea/dnd
   * @param {Object} result.source - Source position
   * @param {Object} result.destination - Destination position
   * @param {number} result.source.index - Source index
   * @param {number} result.destination.index - Destination index
   */
  const handleDragEnd = useCallback(
    (result) => {
      // Do nothing if dropped outside the list
      if (!result.destination) return;

      // Do nothing if dropped in the same position
      if (result.source.index === result.destination.index) return;

      setState((prev) => {
        // Create a copy of the array
        const items = Array.from(prev);

        // Remove item from source position
        const [reorderedItem] = items.splice(result.source.index, 1);

        // Insert item at destination position
        items.splice(result.destination.index, 0, reorderedItem);

        // Call optional callback with reordered array
        if (onReorder) {
          onReorder(items, result);
        }

        return items;
      });
    },
    [setState, onReorder]
  );

  /**
   * Move item up in the list (decrease index)
   * @param {number} currentIndex - Current position of the item
   */
  const moveUp = useCallback(
    (currentIndex) => {
      setState((prev) => {
        const items = Array.from(prev);

        // Validate index
        if (currentIndex <= 0 || currentIndex >= items.length) {
          return prev; // Return unchanged if invalid
        }

        const [item] = items.splice(currentIndex, 1);
        items.splice(currentIndex - 1, 0, item);

        if (onReorder) {
          onReorder(items, { from: currentIndex, to: currentIndex - 1 });
        }

        return items;
      });
    },
    [setState, onReorder]
  );

  /**
   * Move item down in the list (increase index)
   * @param {number} currentIndex - Current position of the item
   */
  const moveDown = useCallback(
    (currentIndex) => {
      setState((prev) => {
        const items = Array.from(prev);

        // Validate index
        if (currentIndex < 0 || currentIndex >= items.length - 1) {
          return prev; // Return unchanged if invalid
        }

        const [item] = items.splice(currentIndex, 1);
        items.splice(currentIndex + 1, 0, item);

        if (onReorder) {
          onReorder(items, { from: currentIndex, to: currentIndex + 1 });
        }

        return items;
      });
    },
    [setState, onReorder]
  );

  /**
   * Move item to specific position
   * @param {number} fromIndex - Source index
   * @param {number} toIndex - Destination index
   */
  const moveTo = useCallback(
    (fromIndex, toIndex) => {
      setState((prev) => {
        const items = Array.from(prev);

        // Validate indices
        if (
          fromIndex < 0 ||
          fromIndex >= items.length ||
          toIndex < 0 ||
          toIndex >= items.length ||
          fromIndex === toIndex
        ) {
          return prev; // Return unchanged if invalid
        }

        const [item] = items.splice(fromIndex, 1);
        items.splice(toIndex, 0, item);

        if (onReorder) {
          onReorder(items, { from: fromIndex, to: toIndex });
        }

        return items;
      });
    },
    [setState, onReorder]
  );

  /**
   * Swap two items in the list
   * @param {number} index1 - First item index
   * @param {number} index2 - Second item index
   */
  const swap = useCallback(
    (index1, index2) => {
      setState((prev) => {
        const items = Array.from(prev);

        // Validate indices
        if (
          index1 < 0 ||
          index1 >= items.length ||
          index2 < 0 ||
          index2 >= items.length ||
          index1 === index2
        ) {
          return prev; // Return unchanged if invalid
        }

        [items[index1], items[index2]] = [items[index2], items[index1]];

        if (onReorder) {
          onReorder(items, { from: index1, to: index2 });
        }

        return items;
      });
    },
    [setState, onReorder]
  );

  return {
    handleDragEnd,
    moveUp,
    moveDown,
    moveTo,
    swap,
  };
};

export default useDragDropReorder;
